import { ArrowLeft, ArrowUp, Camera, MessageSquareText, Lightbulb, MousePointer2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDesignerStore } from '../state/session';
import { useEffect, useRef } from 'react';
function HowStep({
  index,
  icon: Icon,
  title,
  text,
}: {
  index: string;
  icon: typeof Camera;
  title: string;
  text: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="kh-how-step" ref={ref}>
      <span className="kh-how-badge">{index}</span>
      <div className="kh-how-icon">
        <Icon />
      </div>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

export function HomePage() {
  const startFreshDesign = () => useDesignerStore.getState().reset();

  const heroTrackRef = useRef<HTMLDivElement>(null);
  const scrollTopBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const heroTrack = heroTrackRef.current;
    const btn = scrollTopBtnRef.current;
    if (!heroTrack || !btn) return;

    // تریگر را به «رد شدن از هیرو» تغییر دادیم (به‌جای رد شدن از کل بخش
    // سه‌گام) چون هیرو خیلی زودتر تمام می‌شود؛ این یعنی دکمه خیلی زودتر
    // از قبل ظاهر می‌شود و دیگر نیازی به اسکرول طولانی نیست.
    let ticking = false;
    const update = () => {
      const rect = heroTrack.getBoundingClientRect();
      const scrolledPast = rect.bottom < 0;
      btn.classList.toggle('is-visible', scrolledPast);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    window.addEventListener('load', update);
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('load', update);
    };
  }, []);

  useEffect(() => {
    const track = heroTrackRef.current;
    if (!track) return;

    // اگر کاربر انیمیشن کم ترجیح داده، افکت اسکرول-جک رو غیرفعال می‌کنیم
    // و مستقیم حالت «کامل باز شده» عکس رو نشون می‌دیم.
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      track.style.setProperty('--progress', '1');
      return;
    }

    let ticking = false;
    const update = () => {
      const rect = track.getBoundingClientRect();
      // total = فاصله‌ی قابل‌اسکرول داخل این بخش (ارتفاع تراک منهای یک صفحه)
      const total = rect.height - window.innerHeight;
      const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
      track.style.setProperty('--progress', progress.toFixed(4));
      ticking = false;
    };

    // requestAnimationFrame throttling: روی اسکرول سریع فقط یک‌بار در هر فریم اجرا می‌شه
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <main className="karolight-home">
      <style>{`
        .karolight-home {
          /* توکن‌های رنگی برند واقعی کارولایت، نه رنگ‌های ابداعیِ جدید */
          --kh-navy: #1E355B;
          --kh-navy-deep: #0e1a2e;
          --kh-gold: #e8b85f;
          --kh-gold-soft: #f3d9a4;
          --kh-crimson: #a91f2b;
          --kh-cream: #faf5ec;
          --kh-ink: #1c1a18;
          --kh-muted: #6c6259;
          position: relative;
          background: var(--kh-cream);
          color: var(--kh-ink);
          /* عمداً overflow-x:hidden اینجا نمی‌زنیم چون هر مقدار overflow غیر از
             visible باعث می‌شه مرورگر overflow-y رو هم auto حساب کنه و این
             می‌تونه position: sticky داخل صفحه رو خراب کنه. */
        }

        /* ============ هیرو سینمایی (تکنیک اسکرول-پین مثل پورشه) ============ */
        .kh-hero-track {
          position: relative;
          height: 240vh; /* هرچقدر بیشتر، اسکرول کندتر و افکت طولانی‌تر */
        }
        @media (max-width: 720px) {
          .kh-hero-track { height: 170vh; } /* روی موبایل مسیر اسکرول کوتاه‌تر */
        }

        .kh-hero-stage {
          position: sticky;
          top: 0;
          height: 100vh;
          min-height: 560px;
          overflow: hidden;
          display: flex;
          align-items: flex-end;
          background: var(--kh-navy-deep);
        }

        .kh-hero-media { position: absolute; inset: 0; z-index: 0; }
        .kh-hero-media img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          /* زوم اولیه‌ی ۱٫۲۲ کم‌کم به ۱٫۰۸ می‌رسه و همزمان روشن‌تر می‌شه؛
             یعنی هرچی اسکرول می‌کنی، عکس «باز» می‌شه، دقیقا مثل افکت پورشه */
          transform: scale(calc(1.22 - 0.14 * var(--progress, 0)))
                     translateY(calc(var(--progress, 0) * -5%));
          filter: brightness(calc(0.5 + 0.5 * var(--progress, 0)))
                  saturate(calc(0.85 + 0.25 * var(--progress, 0)));
          will-change: transform, filter;
        }

        .kh-hero-vignette {
          position: absolute; inset: 0; z-index: 1;
          background:
            linear-gradient(180deg, rgba(14,26,46,.7) 0%, rgba(14,26,46,.15) 34%, rgba(14,26,46,.1) 55%, rgba(14,26,46,.88) 100%);
          pointer-events: none;
        }

        /* یک پرتوی نور مورب و ظریف، هم برندِ نورپردازی رو یادآوری می‌کنه
           هم با اسکرول کمی پررنگ‌تر می‌شه؛ mix-blend-mode باعث می‌شه روی
           عکس واقعاً شبیه نورِ روی صحنه به نظر برسه، نه یک لایه‌ی جدا */
        .kh-hero-beam {
          position: absolute; inset: -20% -10%; z-index: 1;
          background: conic-gradient(from 200deg at 30% 0%, transparent 0deg, rgba(232,184,95,.18) 18deg, transparent 42deg);
          opacity: calc(0.4 + 0.5 * var(--progress, 0));
          mix-blend-mode: screen;
          pointer-events: none;
        }

        .kh-hero-copy {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 6vw 15vh;
          color: #fff;
          opacity: calc(1.25 - 1.25 * var(--progress, 0));
          transform: translateY(calc(var(--progress, 0) * -26px));
        }

        @media (max-width: 720px) {
          .kh-hero-copy {
            padding-bottom: 30vh;
          }
        }

        .kh-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 14px; border-radius: 999px;
          border: 1px solid rgba(232,184,95,.45);
          background: rgba(232,184,95,.12);
          color: var(--kh-gold-soft);
          font-size: 12px; font-weight: 700;
          margin-bottom: 22px;
        }
        .kh-eyebrow.dark {
          color: var(--kh-crimson);
          border-color: rgba(169,31,43,.25);
          background: rgba(169,31,43,.07);
        }

        .kh-hero-copy h1 {
          font-size: clamp(2.1rem, 5.4vw, 4.4rem);
          line-height: 1.16;
          font-weight: 800;
          letter-spacing: -.02em;
          margin: 0 0 22px;
        }
        .kh-hero-copy h1 em {
          font-style: normal;
          display: block;
          margin-top: 6px;
          background: linear-gradient(100deg, var(--kh-gold-soft), var(--kh-gold) 55%, #fff 120%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .kh-hero-copy > p {
          max-width: 46ch;
          font-size: 1.05rem;
          line-height: 1.9;
          color: rgba(255,255,255,.82);
          margin: 0 0 34px;
        }

        .kh-hero-actions { display: flex; gap: 16px; flex-wrap: wrap; }

        .kh-btn-primary {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 16px 30px; border-radius: 14px;
          font-weight: 700; font-size: 15px;
          background: linear-gradient(135deg, var(--kh-gold), #c9963f);
          color: #241a09;
          box-shadow: 0 16px 40px -12px rgba(232,184,95,.55);
          transition: .25s ease;
        }
        .kh-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 20px 46px -12px rgba(232,184,95,.7); }
        .kh-btn-primary svg { transition: transform .25s ease; }
        .kh-btn-primary:hover svg { transform: translateX(-4px); }

        .kh-btn-ghost {
          display: inline-flex; align-items: center; gap: 9px;
          padding: 16px 22px; border-radius: 14px;
          border: 1px solid rgba(255,255,255,.32);
          color: #fff; font-weight: 600; font-size: 14px;
          background: rgba(255,255,255,.06);
          backdrop-filter: blur(6px);
          transition: .25s ease;
        }
        .kh-btn-ghost:hover { border-color: var(--kh-gold); color: var(--kh-gold-soft); }

        .kh-scroll-cue {
          position: absolute; z-index: 2; bottom: 26px; left: 50%;
          transform: translateX(-50%);
          display: flex; flex-direction: column; align-items: center; gap: 8px;
          color: rgba(255,255,255,.7); font-size: 11px;
          opacity: calc(1 - var(--progress, 0) * 12);
        }

        @media (max-width: 720px) {
          .kh-scroll-cue {
            bottom: 80px;
          }
        }
        .kh-scroll-cue i {
          width: 1px; height: 30px; position: relative; overflow: hidden;
          background: linear-gradient(180deg, rgba(255,255,255,.9), transparent);
        }
        .kh-scroll-cue i::after {
          content: ''; position: absolute; inset: 0;
          background: var(--kh-gold);
          animation: kh-cue-drop 1.8s ease-in-out infinite;
        }
        @keyframes kh-cue-drop {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }

           /* ============ سه گام — ریل نوری متصل ============ */
        .kh-how {
          position: relative; z-index: 2;
          background: var(--kh-cream);
          padding: 11vh 6vw 13vh;
          max-width: 1300px;
          margin: 0 auto;
          overflow: hidden;
        }
        .kh-how > header { text-align: center; max-width: 640px; margin: 0 auto 72px; position: relative; z-index: 1; }
        .kh-how h2 {
          font-size: clamp(1.8rem, 3.2vw, 2.6rem);
          margin: 14px 0 0; font-weight: 800; letter-spacing: -.02em;
        }

        .kh-how::before {
          content: '';
          position: absolute; z-index: 0;
          top: 8%; left: 50%; width: 90vw; height: 90vw; max-width: 900px; max-height: 900px;
          transform: translateX(-50%);
          background: radial-gradient(ellipse at 50% 30%, rgba(232,184,95,.1), transparent 60%);
          pointer-events: none;
        }

        .kh-how-rail {
          position: relative; z-index: 1;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0;
        }

        .kh-how-rail::before {
          content: '';
          position: absolute;
          top: 34px;
          right: calc(100% / 6);
          left: calc(100% / 6);
          height: 2px;
          background: linear-gradient(90deg, var(--kh-crimson), var(--kh-gold), var(--kh-navy));
          opacity: .28;
        }

        .kh-how-step {
          position: relative;
          padding: 0 22px;
          text-align: center;
          opacity: 0;
          transform: translateY(22px);
          transition: opacity .6s ease, transform .6s ease;
        }
        .kh-how-step.is-visible { opacity: 1; transform: translateY(0); }
        .kh-how-step:nth-child(2) { transition-delay: .12s; }
        .kh-how-step:nth-child(3) { transition-delay: .24s; }

        .kh-how-badge {
          position: relative;
          z-index: 2;
          width: 68px; height: 68px;
          margin: 0 auto 26px;
          border-radius: 50%;
          display: grid; place-items: center;
          font: 800 18px/1 Georgia, serif;
          color: #fff;
          background: var(--kh-step-color, var(--kh-crimson));
          box-shadow:
            0 0 0 6px var(--kh-cream),
            0 0 0 9px color-mix(in srgb, var(--kh-step-color, var(--kh-crimson)) 30%, transparent),
            0 14px 30px -8px color-mix(in srgb, var(--kh-step-color, var(--kh-crimson)) 60%, transparent);
        }
        .kh-how-step:nth-child(1) { --kh-step-color: var(--kh-crimson); }
        .kh-how-step:nth-child(2) { --kh-step-color: #c9963f; }
        .kh-how-step:nth-child(3) { --kh-step-color: var(--kh-navy); }

        .kh-how-icon {
          width: 46px; height: 46px;
          margin: 0 auto 18px;
          border-radius: 14px;
          display: grid; place-items: center;
          background: color-mix(in srgb, var(--kh-step-color) 12%, #fff);
          color: var(--kh-step-color);
        }
        .kh-how-icon svg { width: 22px; height: 22px; }

        .kh-how-step h3 { font-size: 1.15rem; margin: 0 0 10px; font-weight: 700; }
        .kh-how-step p {
          color: var(--kh-muted); line-height: 1.85; font-size: .92rem; margin: 0 auto;
          max-width: 30ch;
        }

        @media (max-width: 900px) {
          .kh-how-rail {
            grid-template-columns: 1fr;
            gap: 52px;
          }
          .kh-how-rail::before {
            /* روی موبایل کارت‌ها ارتفاع متغیر دارن (بسته به طول متن)، پس یک خط
               با طول ثابت همیشه از وسط یکی از پاراگراف‌ها رد می‌شود؛ برای همین
               این خط را کامل مخفی می‌کنیم و فقط به شماره‌ی رنگی هر badge و
               ترتیب ورودشان برای نمایش توالی مراحل تکیه می‌کنیم. */
            display: none;
          }
        }
        /* ============ دکمه‌ی بازگشت به بالا ============ */
        .kh-scroll-top {
          position: fixed;
          z-index: 40;
          bottom: clamp(30px, 5vw, 44px);
          left: clamp(18px, 4vw, 32px);
          width: 50px; height: 50px;
          border-radius: 50%;
          display: grid; place-items: center;
          background: linear-gradient(135deg, var(--kh-gold), #c9963f);
          color: #241a09;
          box-shadow: 0 14px 34px -10px rgba(232,184,95,.55);
          border: none;
          cursor: pointer;
          opacity: 0;
          transform: translateY(14px) scale(.9);
          pointer-events: none;
          transition: opacity .3s ease, transform .3s ease;
        }
        .kh-scroll-top.is-visible {
          opacity: 1;
          transform: translateY(0) scale(1);
          pointer-events: auto;
        }
        .kh-scroll-top:hover { transform: translateY(-2px) scale(1.05); }
        .kh-scroll-top svg { width: 22px; height: 22px; }

        @media (max-width: 720px) {
          .kh-scroll-top { width: 44px; height: 44px; }
          .kh-scroll-top svg { width: 19px; height: 19px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .kh-scroll-top { transition: opacity .3s ease; transform: none; }
          .kh-scroll-top.is-visible { transform: none; }
        }

        @media (prefers-reduced-motion: reduce) {
          .kh-how-step { opacity: 1; transform: none; transition: none; }
        }

        /* ============ CTA پایانی — لحظه‌ی روشن‌شدنِ نور ============ */
        .kh-cta {
          position: relative; z-index: 2;
          overflow: hidden;
          margin: 0 auto clamp(36px, 6vw, 72px);
          max-width: 1300px;
          padding: clamp(28px, 5vw, 52px) clamp(22px, 5vw, 56px);
          border-radius: clamp(18px, 3vw, 28px);
          background: radial-gradient(ellipse 120% 100% at 50% 0%, rgba(232,184,95,.16), transparent 55%),
                      linear-gradient(160deg, var(--kh-navy) 0%, var(--kh-navy-deep) 100%);
          color: #fff;
          text-align: center;
        }

        @media (max-width: 720px) {
          .kh-cta {
            margin-left: 16px;
            margin-right: 16px;
            padding: 24px 18px 26px;
          }
        }

        /* مخروط نور از بالا، درست مثل نور یک چراغ سقفی که روی متن می‌تابد */
        .kh-cta-beam {
          position: absolute; z-index: 0;
          top: -6%; left: 50%; width: min(70%, 520px); height: 130%;
          transform: translateX(-50%);
          background: conic-gradient(from 0deg at 50% 0%, transparent 0deg, rgba(232,184,95,.22) 8deg, transparent 26deg, transparent 334deg, rgba(232,184,95,.22) 352deg, transparent 360deg);
          filter: blur(2px);
          animation: kh-cta-flicker 5s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes kh-cta-flicker {
          0%, 100% { opacity: .75; }
          50% { opacity: 1; }
        }

        /* هاله‌ی محیطی پایین که حس نور منعکس‌شده روی کف را می‌دهد */
        .kh-cta::after {
          content: '';
          position: absolute; z-index: 0; inset: auto 0 0 0; height: 55%;
          background: radial-gradient(ellipse 80% 100% at 50% 100%, rgba(232,184,95,.14), transparent 70%);
          pointer-events: none;
        }

        .kh-cta-content { position: relative; z-index: 1; max-width: 640px; margin: 0 auto; }

        .kh-cta .kh-eyebrow {
          color: var(--kh-gold-soft); border-color: rgba(232,184,95,.4); background: rgba(232,184,95,.1);
        }

        .kh-cta h2 {
          font-size: clamp(1.5rem, 3.4vw, 2.3rem);
          margin: 14px 0 22px;
          font-weight: 800;
          line-height: 1.3;
        }

        @media (max-width: 720px) {
          .kh-cta h2 {
            max-width: 280px;
            margin-left: auto;
            margin-right: auto;
          }
        }

        /* دکمه به‌جای یک مستطیل ساده، خودش شبیه یک منبع نور با هاله‌ی نبض‌دار است */
        .kh-cta-glow-btn {
          position: relative;
          display: inline-flex; align-items: center; gap: 10px;
          padding: 17px 34px; border-radius: 999px;
          font-weight: 700; font-size: 15px;
          background: linear-gradient(135deg, var(--kh-gold), #c9963f);
          color: #241a09;
          box-shadow: 0 0 0 0 rgba(232,184,95,.5);
          animation: kh-cta-pulse 2.6s ease-in-out infinite;
          transition: transform .25s ease;
        }
        .kh-cta-glow-btn:hover { transform: translateY(-2px) scale(1.02); }
        .kh-cta-glow-btn svg { transition: transform .25s ease; }
        .kh-cta-glow-btn:hover svg { transform: translateX(-4px); }

        @keyframes kh-cta-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(232,184,95,.45), 0 14px 34px -10px rgba(232,184,95,.5); }
          50% { box-shadow: 0 0 0 14px rgba(232,184,95,0), 0 14px 34px -10px rgba(232,184,95,.5); }
        }

        @media (prefers-reduced-motion: reduce) {
          .kh-cta-beam, .kh-cta-glow-btn { animation: none; }
        }

        @media (prefers-reduced-motion: reduce) {
          .kh-hero-media img, .kh-hero-copy, .kh-hero-beam { transition: none; }
          .kh-scroll-cue i::after { animation: none; }
        }
      `}</style>

      <section className="kh-hero-track" ref={heroTrackRef}>
        <div className="kh-hero-stage">
          <div className="kh-hero-media">
            <picture>
              <source srcSet="/karolight-hero.webp" type="image/webp" />
              <img
                src="/karolight-hero.webp"
                alt="طراحی نورپردازی هوشمند کارو لایت"
                loading="eager"
              />
            </picture>
          </div>
          <div className="kh-hero-vignette" />
          <div className="kh-hero-beam" />

          <div className="kh-hero-copy">
            {/* <span className="kh-eyebrow"><Sparkles size={14} /> تجربه‌ی هوشمند نورپردازی</span> */}
            <h1>
              پیش از خرید
              <em>نور را در خانه‌ی خود ببینید.</em>
            </h1>
            <p>
              عکس فضای خود را بارگذاری کنید تا هوش مصنوعی کارو لایت محصولات را دقیقاً در همان فضا به تصویر بکشد.
            </p>
            <div className="kh-hero-actions">
              <Link to="/design" onClick={startFreshDesign} className="kh-btn-primary">
                <span>شروع طراحی رایگان</span>
                <ArrowLeft size={20} />
              </Link>
              <a href="#how" className="kh-btn-ghost">
                <MousePointer2 size={16} />
                <span>چگونه کار می‌کند؟</span>
              </a>
            </div>
          </div>

          <div className="kh-scroll-cue">
            <span>برای مشاهده اسکرول کنید</span>
            <i />
          </div>
        </div>
      </section>

      <section id="how" className="kh-how">
        <header>
          <span className="kh-eyebrow dark">سه گام تا فضای رویایی</span>
          <h2>از عکس تا واقعیتِ روشن</h2>
        </header>
        <div className="kh-how-rail">
          {[
            { n: '۰۱', icon: Camera, title: 'عکس بگیرید', text: 'یک تصویر واضح از فضای خود آپلود کنید؛ هوش مصنوعی ابعاد و نور محیط را تحلیل می‌کند.' },
            { n: '۰۲', icon: MessageSquareText, title: 'سبک بگویید', text: 'حس‌وحال و کاربری مدنظرتان را در چند کلمه توضیح دهید تا بهترین محصولات پیشنهاد شود.' },
            { n: '۰۳', icon: Lightbulb, title: 'نور را ببینید', text: 'تصویر نهاییِ فضای شما را با محصولات کارو لایت دریافت و با حالت اولیه مقایسه کنید.' },
          ].map((item) => (
            <HowStep key={item.n} index={item.n} icon={item.icon} title={item.title} text={item.text} />
          ))}
        </div>
      </section>

      <section className="kh-cta">
        <div className="kh-cta-beam" aria-hidden="true" />
        <div className="kh-cta-content">
          <span className="kh-eyebrow">آماده‌اید تجربه کنید؟</span>
          <h2>فضای ایده‌آل شما، یک کلیک با نور فاصله دارد.</h2>
          <Link to="/design" onClick={startFreshDesign} className="kh-cta-glow-btn">
            <span>شروع طراحی</span>
            <ArrowLeft size={20} />
          </Link>
        </div>
      </section>

      <button
        type="button"
        className="kh-scroll-top"
        ref={scrollTopBtnRef}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="بازگشت به بالای صفحه"
      >
        <ArrowUp />
      </button>
    </main>
  );
}